import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { AlertTriangle, Check, Copy, Download, ExternalLink, Loader2, Printer, QrCode, RefreshCw } from 'lucide-react';
import { SifaSammelLink, SifaUnterweisung } from '../../services/sifa/types';
import { fetchSammelLink, sammelLinkUrl, setzeSammelLink } from '../../services/sifa/sammellinkService';
import { MELDUNG_ERWEITERUNG_FEHLT } from '../../services/sifa/sifaService';
import { erzeugeAushangPdf } from '../../services/sifa/aushangPdf';
import { ensureEmbeddedFont } from '../../services/pdf/embeddedFont';
import { describeError } from '../../services/errorMessage';
import {
  SAMMEL_AUS_FRAGE,
  SAMMEL_ERKLAERUNG,
  SAMMEL_SCHALTER_HINWEIS,
  sammelAushangDateiname,
  sammelAushangVorgabe,
  sammelQrDateiname,
  sammelSichtbarText,
  zaehleSammelSichtbar,
} from './unterweisungAblageLogic';

interface Props {
  projectId: string;
  /** Die Unterweisungen des Projekts — fuer die Zahl der ueber den Link erreichbaren. */
  unterweisungen: SifaUnterweisung[];
  /** Laedt die Liste noch? Dann keine Zahl — „0 erreichbar" waere nur ein Zwischenstand. */
  unterweisungenLaden?: boolean;
  /** Liste nicht geladen: die Zahl ist unbekannt, nicht 0 — keine Warnung „keine freigeschaltet". */
  unterweisungenFehler?: boolean;
  /** Nur-Ansicht: Link, QR und Aushang ja, schalten nein. */
  isReadOnly?: boolean;
  /** Betreuendes Buero (Fusszeile des Aushangs). */
  bueroName: string;
  /** Kunde = Projektname, klein ueber der Ueberschrift des Aushangs. */
  kundeName: string;
}

type Zustand =
  | { art: 'laedt' }
  /** Nr. 143/144 fehlen — Klartext statt Schalter. */
  | { art: 'fehlt' }
  | { art: 'fehler'; text: string }
  /** link null = noch nie angelegt; token null = aus. */
  | { art: 'da'; link: SifaSammelLink | null };

/**
 * Sammel-Link je Projekt (docs/sifa-ausbau2-plan.md §0.5): EIN QR-Code am
 * Aushang, dahinter alle freigeschalteten Unterweisungen dieses Kunden.
 *
 * Ausschalten fragt nach: der Link und jeder gedruckte QR-Code sind danach
 * tot, ein spaeteres Einschalten vergibt einen NEUEN Token (Service). Ohne
 * Rueckfrage reichte ein Fehlklick, um alle Aushaenge im Betrieb zu
 * entwerten.
 *
 * Ob Nr. 143 fehlt, verraet das Lesen nicht (fail-open null wie „nie
 * angelegt"); erst der Schreibversuch meldet es — dann ersetzt der Klartext
 * den Schalter.
 */
export const SammelLinkKarte: React.FC<Props> = ({
  projectId, unterweisungen, unterweisungenLaden = false, unterweisungenFehler = false, isReadOnly = false, bueroName, kundeName,
}) => {
  /*
   * Jeder Stand traegt das Projekt, zu dem er gehoert. Wechselt projectId
   * (das Modul setzt zusaetzlich key), gilt alles Alte sofort als „laedt" —
   * ohne setState im Effekt. Sonst zeigte die Karte kurz den Schalter des
   * vorigen Kunden, und ein Klick darauf schaltete mit falscher Annahme.
   */
  const [geladen, setGeladen] = useState<{ projectId: string; zustand: Zustand } | null>(null);
  const [ausFragenFuer, setAusFragenFuer] = useState<string | null>(null);
  const [meldungStand, setMeldungStand] = useState<{ projectId: string; ton: 'fehler' | 'info'; text: string } | null>(null);
  const [schaltet, setSchaltet] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  /** QR-Bild samt der Adresse, zu der es gehoert — so zeigt ein neuer Link nie das alte Bild. */
  const [qr, setQr] = useState<{ url: string; dataUrl: string } | null>(null);
  const [vorgabe] = useState(sammelAushangVorgabe);
  const [ueberschrift, setUeberschrift] = useState(vorgabe.ueberschrift);
  const [text, setText] = useState(vorgabe.text);
  const [druckLaeuft, setDruckLaeuft] = useState(false);
  /** Riegel gegen den zweiten Fingertipp — jedes EIN vergibt einen neuen Token. */
  const schaltetRef = useRef(false);

  /** Zaehlt hoch fuer „Erneut laden" — der Effekt liest dann frisch. */
  const [ladeRunde, setLadeRunde] = useState(0);

  useEffect(() => {
    let aktiv = true;
    const pid = projectId;
    fetchSammelLink(pid)
      .then((link) => { if (aktiv) setGeladen({ projectId: pid, zustand: { art: 'da', link } }); })
      .catch((e) => { if (aktiv) setGeladen({ projectId: pid, zustand: { art: 'fehler', text: describeError(e) } }); });
    return () => { aktiv = false; };
  }, [projectId, ladeRunde]);

  const stand: Zustand = geladen && geladen.projectId === projectId ? geladen.zustand : { art: 'laedt' };
  const ausFragen = ausFragenFuer === projectId;
  const meldung = meldungStand && meldungStand.projectId === projectId ? meldungStand : null;
  const zeigeFehler = (text: string) => setMeldungStand({ projectId, ton: 'fehler', text });

  const token = stand.art === 'da' ? stand.link?.token ?? null : null;
  const url = token ? sammelLinkUrl(token) : null;
  const qrDataUrl = qr && qr.url === url ? qr.dataUrl : null;
  /** null = unbekannt (Liste nicht geladen). */
  const anzahl = unterweisungenFehler ? null : zaehleSammelSichtbar(unterweisungen);

  useEffect(() => {
    if (!url) return;
    let aktiv = true;
    const pid = projectId;
    QRCode.toDataURL(url, { width: 480, margin: 2 })
      .then((d) => { if (aktiv) setQr({ url, dataUrl: d }); })
      .catch((e) => { if (aktiv) setMeldungStand({ projectId: pid, ton: 'fehler', text: `QR-Code nicht erzeugt: ${describeError(e)}` }); });
    return () => { aktiv = false; };
  }, [url, projectId]);

  /**
   * Schaltet nur auf einem geladenen Stand DIESES Projekts. Vor dem
   * Einschalten wird frisch gelesen: hat jemand anderes inzwischen
   * eingeschaltet, bliebe sonst sein Link (und jeder Aushang dazu) durch
   * einen neuen Token ohne Rueckfrage entwertet.
   */
  const schalten = async (an: boolean) => {
    if (isReadOnly || schaltetRef.current || stand.art !== 'da') return;
    const pid = projectId;
    schaltetRef.current = true;
    setSchaltet(true);
    setAusFragenFuer(null);
    setMeldungStand(null);
    try {
      if (an) {
        const frisch = await fetchSammelLink(pid);
        if (frisch?.token) {
          setGeladen({ projectId: pid, zustand: { art: 'da', link: frisch } });
          setMeldungStand({ projectId: pid, ton: 'info', text: 'Der Sammel-Link war schon eingeschaltet (von anderer Stelle) — er bleibt unverändert.' });
          return;
        }
      }
      const link = await setzeSammelLink(pid, an);
      setGeladen({ projectId: pid, zustand: { art: 'da', link } });
    } catch (e) {
      // Der Service wirft genau diesen Text, wenn Nr. 143/144 fehlen.
      if (e instanceof Error && e.message === MELDUNG_ERWEITERUNG_FEHLT) setGeladen({ projectId: pid, zustand: { art: 'fehlt' } });
      else setMeldungStand({ projectId: pid, ton: 'fehler', text: describeError(e) });
    } finally {
      schaltetRef.current = false;
      setSchaltet(false);
    }
  };

  const kopieren = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setKopiert(true);
      window.setTimeout(() => setKopiert(false), 2000);
    } catch {
      // Zwischenablage gesperrt (unsicherer Kontext): der Link steht darunter zum Markieren.
      zeigeFehler('Kopieren war nicht möglich — bitte den Link unten markieren und kopieren.');
    }
  };

  const aushangErzeugen = async () => {
    if (!url || druckLaeuft) return;
    setDruckLaeuft(true);
    setMeldungStand(null);
    try {
      // Eigener, groesserer Code fuer den Druck: 480 px werden auf 12 cm Papier weich.
      const qrDruck = await QRCode.toDataURL(url, { width: 1200, margin: 1 });
      await ensureEmbeddedFont();
      const bytes = erzeugeAushangPdf({
        ueberschrift,
        text,
        url,
        qrDataUrl: qrDruck,
        bueroName,
        kundeName,
        unterweisungTitel: 'Alle freigeschalteten Unterweisungen',
        artLabel: 'Unterweisungen',
      });
      const { saveAs } = await import('file-saver');
      saveAs(new Blob([bytes], { type: 'application/pdf' }), sammelAushangDateiname(kundeName));
    } catch (e) {
      zeigeFehler(describeError(e));
    } finally {
      setDruckLaeuft(false);
    }
  };

  const aktion = 'min-h-[40px] px-3 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40';
  const feld = 'mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-200 disabled:bg-slate-50';

  const schalter = () => {
    if (stand.art !== 'da') return null;
    const an = !!token;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={an}
        aria-label={an ? 'Sammel-Link ausschalten' : 'Sammel-Link einschalten'}
        onClick={() => (an ? setAusFragenFuer(projectId) : void schalten(true))}
        disabled={isReadOnly || schaltet}
        title={isReadOnly ? 'Nur-Ansicht: der Sammel-Link lässt sich nicht schalten.' : undefined}
        className="shrink-0 min-h-[40px] flex items-center gap-2 disabled:opacity-50"
      >
        <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${an ? 'bg-teal-600' : 'bg-slate-300'}`}>
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${an ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </span>
        <span className="text-xs font-bold text-slate-700 w-6 text-left">
          {schaltet ? <Loader2 className="w-4 h-4 animate-spin text-teal-600" /> : an ? 'An' : 'Aus'}
        </span>
      </button>
    );
  };

  return (
    <div className="bg-white border border-teal-200 rounded-2xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-teal-50 rounded-xl shrink-0"><QrCode className="w-5 h-5 text-teal-700" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-slate-800">Alle Unterweisungen per QR-Code</h2>
          <p className="text-xs text-slate-500">{SAMMEL_ERKLAERUNG}</p>
        </div>
        {schalter()}
      </div>

      {stand.art === 'laedt' && (
        <div className="py-2 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
      )}

      {stand.art === 'fehlt' && (
        <p className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
          Der Sammel-Link steht erst nach der Datenbank-Erweiterung Nr. 143/144 zur Verfügung. Die einzelnen Kundenlinks funktionieren wie bisher.
        </p>
      )}

      {stand.art === 'fehler' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span className="flex-1">Der Sammel-Link ließ sich nicht laden: {stand.text}</span>
          <button type="button" onClick={() => { setGeladen(null); setLadeRunde((n) => n + 1); }} className="flex items-center gap-1 font-bold underline">
            <RefreshCw className="w-3.5 h-3.5" /> Erneut laden
          </button>
        </div>
      )}

      {stand.art === 'da' && (
        <>
          {!isReadOnly && <p className="text-[11px] text-slate-500">{SAMMEL_SCHALTER_HINWEIS}</p>}

          {ausFragen && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl space-y-2">
              <p className="text-xs text-amber-900">{SAMMEL_AUS_FRAGE}</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setAusFragenFuer(null)} className="min-h-[40px] px-4 text-xs font-semibold text-slate-600 hover:bg-white rounded-xl">Abbrechen</button>
                <button type="button" onClick={() => void schalten(false)} disabled={schaltet} className="min-h-[40px] px-4 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl disabled:opacity-40">
                  Ausschalten
                </button>
              </div>
            </div>
          )}

          {!unterweisungenLaden && (
            <p className={`text-xs flex items-start gap-1.5 ${anzahl === 0 ? 'text-amber-800' : 'text-slate-600'}`}>
              {anzahl === 0 && <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />}
              <span>{sammelSichtbarText(anzahl)}</span>
            </p>
          )}

          {url && (
            <div className="grid grid-cols-1 sm:grid-cols-[168px_1fr] gap-4">
              <div className="space-y-2">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR-Code zum Sammel-Link" className="w-full max-w-[168px] mx-auto rounded-lg border border-slate-200" />
                ) : (
                  <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
                )}
                {qrDataUrl && (
                  <a href={qrDataUrl} download={sammelQrDateiname(kundeName)} className={`${aktion} w-full text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                    <Download className="w-3.5 h-3.5" /> QR als PNG
                  </a>
                )}
              </div>

              <div className="space-y-3 min-w-0">
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => void kopieren()} className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                    {kopiert ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} {kopiert ? 'Kopiert' : 'Link kopieren'}
                  </button>
                  <button type="button" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')} className={`${aktion} text-slate-700 bg-white border border-slate-200 hover:bg-slate-50`}>
                    <ExternalLink className="w-3.5 h-3.5" /> Öffnen
                  </button>
                  <button type="button" onClick={() => void aushangErzeugen()} disabled={druckLaeuft} className={`${aktion} text-white bg-teal-600 hover:bg-teal-700`}>
                    {druckLaeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Aushang als PDF
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 break-all select-all" title="Sammel-Link">{url}</p>

                <details>
                  <summary className="cursor-pointer text-xs font-bold text-slate-600 select-none">Aushang-Text anpassen</summary>
                  <div className="mt-2 space-y-2">
                    <label className="block">
                      <span className="text-xs font-bold text-slate-600">Überschrift</span>
                      <input value={ueberschrift} onChange={(e) => setUeberschrift(e.target.value)} maxLength={120} className={feld} />
                    </label>
                    <label className="block">
                      <span className="text-xs font-bold text-slate-600">Kurzer Text</span>
                      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={600} className={`${feld} resize-y`} />
                      <span className="block text-[10px] text-slate-400 mt-1">Leerzeile = neuer Absatz. Der Link steht zusätzlich als Text unter dem Code.</span>
                    </label>
                  </div>
                </details>
              </div>
            </div>
          )}

          {!url && (
            <p className="text-xs text-slate-500">
              {isReadOnly ? 'Der Sammel-Link ist ausgeschaltet.' : 'Der Sammel-Link ist ausgeschaltet. Einschalten erzeugt einen Link mit QR-Code für den Aushang.'}
            </p>
          )}
        </>
      )}

      {meldung && (
        <p
          className={`text-xs flex items-start gap-1.5 ${meldung.ton === 'fehler' ? 'text-red-700' : 'text-blue-800'}`}
          role={meldung.ton === 'fehler' ? 'alert' : 'status'}
        >
          {meldung.ton === 'fehler' && <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />} {meldung.text}
        </p>
      )}
    </div>
  );
};
